export const ROOM_UNIFORMITY_METRICS = [
     ["canopy.air_temperature", "temperature", "°C"],
     ["canopy.relative_humidity", "humidity", "%"],
     ["ambient.co2", "co2", "ppm"],
     ["canopy.leaf_vpd", "vpd", "kPa"],
     ["light.ppfd", "ppfd", "µmol/m²/s"],
     ["light.illuminance", "lux", "lx"],
     ["substrate.vwc", "vwc", "%"],
     ["substrate.temperature", "substrateTemperature", "°C"],
     ["substrate.bulk_ec", "bulkEc", "mS/cm"],
     ["substrate.pore_water_ec", "poreEc", "mS/cm"],
].map(([code, key, unit]) => ({ code, key, unit }))

export const ROOM_UNIFORMITY_AGGREGATION_METHOD = "per_node_bucket_mean_then_cross_node_median_min_max_spread"

export const ROOM_UNIFORMITY_BUCKET_SECONDS = Object.freeze({
     "1h": 60,
     "24h": 300,
     "7d": 1800,
     "30d": 7200,
})

const ROOM_UNIFORMITY_QUALITY_FLAGS = new Set([
     "valid",
     "estimated",
     "uncalibrated",
     "stale",
     "invalid",
     "unverified",
])

const ROOM_EVIDENCE_STATE_FLAGS = [
     "invalid",
     "stale",
     "unverified",
     "uncalibrated",
]

const normalizedRoomQualityFlags = (value) => Array.from(new Set(
     (Array.isArray(value) ? value : [])
          .filter((flag) => typeof flag === "string" && flag.trim())
          .map((flag) => flag.trim().toLowerCase()),
)).sort()

const roomEvidenceStateFlags = (flags) => ROOM_EVIDENCE_STATE_FLAGS.filter((flag) => flags.includes(flag))

const roomEvidenceDetails = (uniformity, flags) => [
     typeof uniformity?.source === "string" && `source=${uniformity.source}`,
     uniformity?.read_only !== undefined && `readOnly=${String(uniformity.read_only)}`,
     uniformity?.controls_hardware !== undefined && `controlsHardware=${String(uniformity.controls_hardware)}`,
     typeof uniformity?.variable_code === "string" && `variableCode=${uniformity.variable_code}`,
     typeof uniformity?.range === "string" && `range=${uniformity.range}`,
     typeof uniformity?.aggregation?.method === "string" && `method=${uniformity.aggregation.method}`,
     typeof uniformity?.aggregation?.time_basis === "string" && `timeBasis=${uniformity.aggregation.time_basis}`,
     uniformity?.aggregation?.is_direct_measurement !== undefined
          && `isDirectMeasurement=${String(uniformity.aggregation.is_direct_measurement)}`,
     uniformity?.aggregation?.fills_missing_buckets !== undefined
          && `fillsMissingBuckets=${String(uniformity.aggregation.fills_missing_buckets)}`,
     uniformity?.aggregation?.interpolates_space !== undefined
          && `interpolatesSpace=${String(uniformity.aggregation.interpolates_space)}`,
     flags.length && `qualityFlags=${flags.join(",")}`,
].filter(Boolean)

export const roomAggregateEvidence = (uniformity, { variableCode, range } = {}) => {
     const rawFlags = uniformity?.aggregation?.quality_flags
     const flags = normalizedRoomQualityFlags(rawFlags)
     const stateFlags = roomEvidenceStateFlags(flags)
     const qualityFlagsKnown = Array.isArray(rawFlags)
          && rawFlags.every((flag) => (
               typeof flag === "string"
               && ROOM_UNIFORMITY_QUALITY_FLAGS.has(flag)
          ))
     const expectedBucketSeconds = ROOM_UNIFORMITY_BUCKET_SECONDS[range]
     const contractKnown = uniformity?.source === "feeder_research_observations"
          && Number.isFinite(expectedBucketSeconds)
          && uniformity?.read_only === true
          && uniformity?.controls_hardware === false
          && uniformity?.variable_code === variableCode
          && uniformity?.range === range
          && uniformity?.aggregation?.method === ROOM_UNIFORMITY_AGGREGATION_METHOD
          && uniformity?.aggregation?.time_basis === "source_observed_at"
          && uniformity?.aggregation?.is_direct_measurement === false
          && uniformity?.aggregation?.fills_missing_buckets === false
          && uniformity?.aggregation?.interpolates_space === false
          && uniformity?.aggregation?.bucket_seconds === expectedBucketSeconds
          && qualityFlagsKnown
     const kind = contractKnown ? "calculated" : "unknown"
     const trustedStateFlags = contractKnown ? stateFlags : []
     return {
          kind,
          state: trustedStateFlags[0] || (contractKnown ? "valid" : "unknown"),
          badges: [kind, ...trustedStateFlags],
          flags,
          details: roomEvidenceDetails(uniformity, flags),
          descriptionKey: "sensor.evidenceAria",
          calibrationDocumented: false,
     }
}

export const roomEvidenceValue = (value, evidence) => {
     if (evidence?.flags?.includes("invalid")) return null
     if ((typeof value !== "number" && typeof value !== "string")
          || (typeof value === "string" && !value.trim())) return null
     const parsed = Number(value)
     return Number.isFinite(parsed) ? parsed : null
}

export const roomDisplayUnit = (unit) => typeof unit === "string" && unit.trim() ? unit.trim() : null

export const roomNodeDisplayValue = (nodeValue, evidence) => {
     const value = roomEvidenceValue(nodeValue?.value, evidence)
     const unit = roomDisplayUnit(nodeValue?.unit)
     return value === null || unit === null
          ? { value: null, unit: null }
          : { value, unit }
}

export const roomNodeValueEvidence = (nodeValue) => {
     const flags = normalizedRoomQualityFlags(nodeValue?.quality_flags)
     const stateFlags = roomEvidenceStateFlags(flags)
     return {
          kind: "unknown",
          state: stateFlags[0] || "unknown",
          badges: ["unknown", ...stateFlags],
          flags,
          details: flags.length ? [`qualityFlags=${flags.join(",")}`] : [],
          descriptionKey: "sensor.evidenceAria",
          calibrationDocumented: false,
     }
}

export const roomDeployments = (research) => (research?.deployments || []).map((deployment) => {
     const versions = (deployment.setup_versions || []).filter((version) => {
          const kind = version?.geometry?.monitoring_space_kind
          return kind === "commercial_room" || kind === "greenhouse"
     })
     return versions.length ? { ...deployment, roomSetupVersions: versions } : null
}).filter(Boolean)

export const latestRoomPoint = (uniformity) => {
     const points = Array.isArray(uniformity?.points) ? uniformity.points : []
     return points.length ? points[points.length - 1] : null
}

export const roomPlacementPosition = (placement, geometry) => {
     const length = Number(geometry?.length_m)
     const width = Number(geometry?.width_m)
     const x = Number(placement?.x_m)
     const y = Number(placement?.y_m)
     if (![length, width, x, y].every(Number.isFinite) || length <= 0 || width <= 0) return null
     return {
          left: `${Math.max(0, Math.min(100, x / length * 100))}%`,
          top: `${Math.max(0, Math.min(100, y / width * 100))}%`,
     }
}

export const roomNodeValueMap = (point) => new Map(
     (point?.node_values || []).map((item) => [item.node_public_id, item]),
)

export const roomTrendSegments = (uniformity) => {
     if (normalizedRoomQualityFlags(uniformity?.aggregation?.quality_flags).includes("invalid")) return []
     const expectedBucketSeconds = ROOM_UNIFORMITY_BUCKET_SECONDS[uniformity?.range]
     if (!Number.isFinite(expectedBucketSeconds)
          || uniformity?.aggregation?.bucket_seconds !== expectedBucketSeconds) return []
     const bucketMilliseconds = expectedBucketSeconds * 1000
     const candidates = (uniformity?.points || []).reduce((result, point) => {
          if (typeof point?.bucket_at !== "string" || !point.bucket_at.trim() || point?.unit_consistent === false) return result
          const sampledAt = new Date(point.bucket_at).getTime()
          const unit = roomDisplayUnit(point.unit)
          if ([point.minimum, point.median, point.maximum].some((value) => value === null || value === undefined || value === "")) return result
          const values = [Number(point.minimum), Number(point.median), Number(point.maximum)]
          if (!Number.isFinite(sampledAt) || !values.every(Number.isFinite) || unit === null) return result
          result.push({ ...point, sampledAt, unit, minimum: values[0], median: values[1], maximum: values[2] })
          return result
     }, [])
     if (new Set(candidates.map((point) => point.unit)).size !== 1) return []
     const segments = []
     candidates.forEach((normalized) => {
          const current = segments[segments.length - 1]
          const previous = current?.[current.length - 1]
          if (!current || normalized.sampledAt - previous.sampledAt > bucketMilliseconds * 1.5) {
               segments.push([normalized])
          } else {
               current.push(normalized)
          }
     })
     return segments
}

const ROOM_NODE_TYPES = new Set([
     "chuck_environment_station",
     "vwc_controller",
])

export const roomNodeRole = (nodeType) => ({
     chuck_environment_station: "environment_station",
     vwc_controller: "substrate_sensor",
     leaf_sensor: "plant_sensor",
     manual_import: "reference_sensor",
}[nodeType] || "reference_sensor")

export const roomCandidateNodes = (research, deploymentId) => {
     const selectedId = Number(deploymentId)
     const activeMembership = new Map()
     ;(research?.deployments || []).filter((deployment) => !deployment.ended_at).forEach((deployment) => {
          ;(deployment.nodes || []).filter((node) => !node.unlinked_at).forEach((node) => {
               activeMembership.set(Number(node.id), Number(deployment.id))
          })
     })
     return (research?.nodes || []).filter((node) => (
          Boolean(node.active)
          && ROOM_NODE_TYPES.has(node.node_type)
          && (!activeMembership.has(Number(node.id)) || activeMembership.get(Number(node.id)) === selectedId)
     ))
}

const finiteNumber = (value) => {
     if (value === "" || value === null || value === undefined) return null
     const parsed = Number(value)
     return Number.isFinite(parsed) ? parsed : null
}

export const validateRoomLayoutDraft = (draft) => {
     const errors = []
     const dimensions = [finiteNumber(draft?.lengthM), finiteNumber(draft?.widthM), finiteNumber(draft?.heightM)]
     const placements = (draft?.placements || []).filter((item) => item.selected)
     if (!Number.isInteger(Number(draft?.deploymentId)) || Number(draft?.deploymentId) <= 0) errors.push({ code: "deployment" })
     if (String(draft?.layoutName || "").trim().length < 2) errors.push({ code: "layoutName" })
     if (!dimensions.every((value) => value !== null && value > 0)) errors.push({ code: "dimensions" })
     if (!placements.length) errors.push({ code: "nodes" })
     const identities = placements.map((item) => item.nodePublicId).filter(Boolean)
     if (identities.length !== placements.length) errors.push({ code: "nodeIdentity" })
     if (new Set(identities).size !== identities.length) errors.push({ code: "duplicateNode" })
     placements.forEach((placement) => {
          const coordinates = [finiteNumber(placement.xM), finiteNumber(placement.yM), finiteNumber(placement.zM)]
          if (!coordinates.every((value) => value !== null && value >= 0)) {
               errors.push({ code: "coordinate", node: placement.label || placement.nodePublicId })
          } else if (dimensions.every((value) => value !== null && value > 0)
               && (coordinates[0] > dimensions[0] || coordinates[1] > dimensions[1] || coordinates[2] > dimensions[2])) {
               errors.push({ code: "bounds", node: placement.label || placement.nodePublicId })
          }
     })
     return { valid: errors.length === 0, errors }
}

export const buildRoomSetupPayload = (draft) => {
     const validation = validateRoomLayoutDraft(draft)
     if (!validation.valid) throw new Error("invalid room layout draft")
     return {
          schema_version: 1,
          setup_kind: draft.spaceKind === "greenhouse" ? "greenhouse" : "other",
          growth_medium: "unknown",
          lighting_type: draft.lightingType || "unknown",
          geometry_evidence: draft.geometryEvidence || "not_recorded",
          effective_at: draft.effectiveAt || null,
          notes: String(draft.notes || "").trim(),
          geometry: {
               monitoring_space_kind: draft.spaceKind === "greenhouse" ? "greenhouse" : "commercial_room",
               layout_name: String(draft.layoutName).trim(),
               length_m: Number(draft.lengthM),
               width_m: Number(draft.widthM),
               height_m: Number(draft.heightM),
               node_placements: draft.placements.filter((item) => item.selected).map((item) => ({
                    node_public_id: item.nodePublicId,
                    x_m: Number(item.xM),
                    y_m: Number(item.yM),
                    z_m: Number(item.zM),
                    measurement_height_m: Number(item.zM),
                    height_reference: "floor",
                    zone_label: String(item.zoneLabel || "").trim(),
                    mounting_context: item.mountingContext || "unknown",
                    position_evidence: draft.positionEvidence || "not_recorded",
               })),
          },
     }
}
